import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import { ConfirmDeleteDialogComponent } from '../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import { AdItem, AdminApiService } from 'src/app/shared/services/admin-api.service';

@Component({
  standalone: false,
  selector: 'app-manage-ads',
  templateUrl: './manage-ads.component.html',
  styleUrls: ['./manage-ads.component.scss'],
})
export class ManageAdsComponent extends AdminSectionBaseComponent implements OnInit {
  ads: AdItem[] = [];
  adDialogOpen = false;
  adsSearchTerm = '';
  adsTypeFilter: 'all' | 'image' | 'video' = 'all';
  adMode: 'upload' | 'url' = 'upload';
  adMediaType: 'image' | 'video' = 'image';
  adLink = '';
  adImageUrl = '';
  selectedAdFile: File | null = null;
  selectedAdFileName = '';
  adThumbnailUrl = '';
  selectedAdThumbnailFile: File | null = null;
  selectedAdThumbnailFileName = '';

  private adsView: { src: AdItem[] | null; type: string; q: string; value: AdItem[] } = { src: null, type: '', q: '', value: [] };

  constructor(
    private adminApiService: AdminApiService,
    private dialog: MatDialog,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
  }

  ngOnInit(): void {
    this.refreshAds();
  }

  refreshAds(): void {
    this.trackApiCall(this.adminApiService.listAds(this.token)).subscribe({
      next: (ads) => { this.ads = ads; },
      error: (error) => this.handleError(error),
    });
  }

  getDisplayedAds(): AdItem[] {
    if (this.adsView.src === this.ads && this.adsView.type === this.adsTypeFilter && this.adsView.q === this.adsSearchTerm) {
      return this.adsView.value;
    }
    let result = this.ads;
    if (this.adsTypeFilter !== 'all') {
      result = result.filter((ad) => (ad.type === 'video' ? 'video' : 'image') === this.adsTypeFilter);
    }
    const q = this.adsSearchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((ad) =>
        ad.image.toLowerCase().includes(q) || ad.link.toLowerCase().includes(q)
      );
    }
    this.adsView = { src: this.ads, type: this.adsTypeFilter, q: this.adsSearchTerm, value: result };
    return result;
  }

  isVideoAd(ad: AdItem): boolean {
    return ad.type === 'video';
  }

  adPreviewImage(ad: AdItem): string {
    return ad.type === 'video' ? (ad.thumbnail || ad.image) : ad.image;
  }

  setAdMode(mode: 'upload' | 'url'): void {
    this.adMode = mode;
  }

  setAdMediaType(type: 'image' | 'video'): void {
    this.adMediaType = type;
  }

  openCreateAdDialog(): void {
    this.resetAdForm();
    this.adDialogOpen = true;
  }

  closeCreateAdDialog(): void {
    this.adDialogOpen = false;
    this.resetAdForm();
  }

  onAdFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    this.selectedAdFile = file;
    this.selectedAdFileName = file ? file.name : '';
  }

  onAdThumbnailSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    this.selectedAdThumbnailFile = file;
    this.selectedAdThumbnailFileName = file ? file.name : '';
  }

  adSubmitActionKey(): string { return 'ad-submit'; }
  adDeleteActionKey(image: string): string { return `ad-delete:${image}`; }

  submitAd(): void {
    const isVideo = this.adMediaType === 'video';
    const link = this.adLink.trim();
    // Video ads play in-place and do not open an external link, so only images require one.
    if (!isVideo && !link) {
      this.showError('A link URL is required.');
      return;
    }

    const actionKey = this.adSubmitActionKey();
    if (this.adMode === 'url') {
      const image = this.adImageUrl.trim();
      if (!image) {
        this.showError(isVideo ? 'A video URL is required.' : 'An image URL is required.');
        return;
      }
      const thumbnail = this.adThumbnailUrl.trim();
      if (isVideo && !thumbnail) {
        this.showError('A thumbnail image URL is required for video ads.');
        return;
      }
      this.trackActionCall(actionKey, this.adminApiService.addAdByUrl(this.token, image, link, this.adMediaType, thumbnail)).subscribe({
        next: (ads) => { this.ads = ads; this.closeCreateAdDialog(); this.showSuccess('Ad added successfully.'); },
        error: (error) => this.handleError(error),
      });
    } else {
      if (!this.selectedAdFile) {
        this.showError(isVideo ? 'Please choose a video file to upload.' : 'Please choose an image file to upload.');
        return;
      }
      if (isVideo && !this.selectedAdThumbnailFile) {
        this.showError('Please choose a thumbnail image for the video ad.');
        return;
      }
      this.trackActionCall(actionKey, this.adminApiService.uploadAd(this.token, this.selectedAdFile, link, this.adMediaType, this.selectedAdThumbnailFile || undefined)).subscribe({
        next: (ads) => { this.ads = ads; this.closeCreateAdDialog(); this.showSuccess('Ad uploaded successfully.'); },
        error: (error) => this.handleError(error),
      });
    }
  }

  removeAd(ad: AdItem): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Remove Ad',
        message: 'Are you sure you want to permanently remove this ad?',
        itemName: ad.image || 'Ad'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.adDeleteActionKey(ad.image);
        this.trackActionCall(actionKey, this.adminApiService.deleteAd(this.token, ad.image)).subscribe({
          next: (ads) => { this.ads = ads; this.showSuccess('Ad removed successfully.'); },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  private resetAdForm(): void {
    this.adLink = '';
    this.adImageUrl = '';
    this.selectedAdFile = null;
    this.selectedAdFileName = '';
    this.adThumbnailUrl = '';
    this.selectedAdThumbnailFile = null;
    this.selectedAdThumbnailFileName = '';
    this.adMediaType = 'image';
  }

  trackByAdImage(_index: number, ad: AdItem): string {
    return ad.image;
  }
}
